import { Dialog, DialogProps, DomEvent, Textbox, TextboxVariant, Tree, TreeNode } from "@mcleod/components";
import { ArrayUtil, ObjectUtil } from "@mcleod/core";
import { ImageName } from "@mcleod/images";
import { DatabaseMenuItem } from "./DatabaseMenuItem";
import { MenuEditorTree } from "./MenuEditorTree";
import { MenuEditorTreeNode } from "./MenuEditorTreeNode";
import { MenuEditorUtil } from "./MenuEditorUtil";

interface MenuBranchSelectorProps {
    excludedNode: MenuEditorTreeNode;
    additionalExcludeFilter?: (menuItem: DatabaseMenuItem) => boolean;
    dialogProps?: Partial<DialogProps>;
}

export class MenuBranchSelector extends Dialog {
    private rootData: DatabaseMenuItem;
    private excludedGroupIds: Set<string>;
    private tree: Tree<MenuBranchSelectorTreeNode>;
    private sourceTree: MenuEditorTree;
    private filterTextbox: Textbox;
    private excludedNode: MenuEditorTreeNode;
    private additionalExcludeFilter: (menuItem: DatabaseMenuItem) => boolean;
    private excludeParentPath: string;

    constructor(props: MenuBranchSelectorProps) {
        super({
            width: 700,
            title: "Select Submenu",
            resizable: true,
            panelTitleProps: { backgroundColor: "primary.darker", color: "primary.reverse" },
            panelContentProps: { paddingLeft: 12, paddingRight: 12 },
            ...props.dialogProps
        });
        this.excludedNode = props.excludedNode;
        this.additionalExcludeFilter = props.additionalExcludeFilter;
        this.sourceTree = this.excludedNode.tree;
        this.addContent();

    }
    addContent() {
        this.filterTextbox = new Textbox({
            captionVisible: false,
            placeholder: "Filter Submenus",
            imagePostName: ImageName.search,
            variant: TextboxVariant.UNDERLINED
        });
        this.excludedGroupIds = this.determineExcludedGroupIds();

        const rootDataItems = this.sourceTree.getRootNode().getChildren().map((node: MenuEditorTreeNode) => node.menuItem.createCopy());
        this.rootData = new DatabaseMenuItem({ items: rootDataItems, caption: "", path: "", image: "" });
        this.filterExcludedItems(this.rootData );

        this.add(this.filterTextbox);
        this.filterTextbox.addChangeListener(() => this.populateTree());
        this.buttonOK.enabled = false;
        this.addTree();
    }

    filterExcludedItems(menuItem: DatabaseMenuItem) {
        for (let i = menuItem?.items?.length - 1; i >= 0; i--) {
            const child = menuItem.items[i];
            if (this.shouldExcludeItem(child)) {
                menuItem.items.splice(i, 1);
            } else {
                this.filterExcludedItems(child);
            }
        }
    }

    private shouldExcludeItem(menuItem: DatabaseMenuItem) {
        const result = this.excludedGroupIds.has(menuItem.groupData.get("id"))
        || ArrayUtil.isEmptyArray(menuItem.items);

        if (result == false && this.additionalExcludeFilter) {
            return this.additionalExcludeFilter(menuItem);
        }
        return result;
    }

    private determineExcludedGroupIds(): Set<string> {
        const result = new Set<string>();
        if (!this.excludedNode.isLeaf()) {
            // Exclude this branch and all it's decendant branches
            result.add(this.excludedNode.menuItem.groupData.get("id"));
            this.excludedNode.recurseChildren((node: MenuEditorTreeNode) => {
                if (!node.isLeaf()) {
                    result.add(node.menuItem.groupData.get("id"));
                }
            });
            this.excludeParentPath = this.excludedNode.parent?.getPathAsString();
        } else if (this.excludedNode.parent) {
            // Exclude the parent of the leaf node
            const parentNode = this.excludedNode.parent as MenuBranchSelectorTreeNode;
            result.add(parentNode.menuItem?.groupData?.get("id"));
        }
        return result;
    }

    private addTree() {
        this.tree = new Tree({
            fillHeight: true,
            fillRow: true,
            selectedNodeBackgroundColor: "component.palette.hover.backgroundColor",
            selectedNodeColor: (node) => node.color,
        });
        this.populateTree();
        this.tree.addChangeListener(() => {
            const selectedNode = this.tree.selectedNode;
            if (this.excludeParentPath) {
                this.buttonOK.enabled = selectedNode && selectedNode.getPathAsString() != this.excludeParentPath;
            } else {
                this.buttonOK.enabled = selectedNode != null;
            }
        });
        this.add(this.tree);
    }

    populateTree() {
        const root = new TreeNode({ tree: this.tree });
        const filtered = this.getFilteredItems();
        for (const item of filtered)
            this.addTreeNode(root, item);
        this.tree.getRootNode().setChildren(root.getChildren());
        this.tree.selectedNode = null;
    }

    private getFilteredItems(): DatabaseMenuItem[] {
        const filter = this.filterTextbox.text.toLowerCase();
        let filtered = this.rootData.items;
        if (filter?.length > 0) {
            filtered = filtered.map(item => item.createCopy());
            filtered = ObjectUtil.filterRecursiveArrayContainingString<DatabaseMenuItem>(filtered, "_items", "_caption", filter);
        }
        return filtered;
    }

    private addTreeNode(parent: TreeNode, menuItem: DatabaseMenuItem) {
        const node = new MenuBranchSelectorTreeNode(menuItem);
        parent.addChild(node);
        if (this.excludeParentPath && node.getPathAsString() == this.excludeParentPath) {
            node.selectable = false;
        }
        for (const child of menuItem.items)
            this.addTreeNode(node, child);
    }

    async show(): Promise<MenuEditorTreeNode> {
        const confirmed = await super.show();
        if (!confirmed || !this.tree.selectedNode) {
            return null;
        }

        const key = this.tree.selectedNode?.menuItem.compositeKey;
        return this.sourceTree.findFirstNode(node => node.menuItem.compositeKey === key);
    }
}


export class MenuBranchSelectorTreeNode extends TreeNode {
    menuItem: DatabaseMenuItem;
    constructor(menuItem: DatabaseMenuItem) {
        super({
            text: menuItem.caption,
            fontSize: "small",
            fontBold: true,
            allowSelect: false,
            rowBreak: false,
            fillRow: true,
            imageMarginLeft: 0,
            themeKey: "menu.branchNode",
        });
        this.menuItem = menuItem;
        this.label.height = 24;
        this.expandOnClick = false;
        this.expanded = true;
        MenuEditorUtil.addSharedMenuIndicator(this.label, this);
    }

    protected nodeDoubleClicked(event: DomEvent) {
        event.stopPropagation();
    }

    override addChild(...nodes: TreeNode[]): TreeNode {
        const result = super.addChild(...nodes);
        this.label.imageRotation = 0;
        return result;
    }
}
